// amount.ts — IS WHAT THE OPERATOR TYPED A NUMBER THE BOOKS CAN POST?
//
// The posting forms take an amount as text and three of them only checked that
// the box was not empty. `Number('1,000')` is NaN, and JSON.stringify writes
// NaN as null, so an Indian-grouped figure reached the API as
// `"amount": null` and came back as "The books hit a fault: toMinor: not a
// plain decimal string" — a 500 telling the operator to contact support for
// what is a comma. A minus sign fared worse: it posted, and the entry ran
// backwards.
//
// One rule and one sentence for every amount box, so the mistake is named at
// the box it was made in and nothing is sent. The shape is deliberately the
// ledger's own: bff/src/lib/money.ts::toMinor accepts a plain decimal string
// and nothing else.

const PLAIN_DECIMAL = /^-?(\d+(\.\d*)?|\.\d+)$/;
const NOT_A_NUMBER = 'Amount must be a plain number — no commas, no spaces, no letters.';

/**
 * The sentence to show under a REQUIRED amount box, or null when the text is
 * postable. A blank box is not a mistake yet — the submit button is what says
 * the form is unfinished, so silence here keeps a red line off a fresh screen.
 */
export function amountProblem(text: string): string | null {
  const t = text.trim();
  if (t === '') return null;
  if (!PLAIN_DECIMAL.test(t)) return NOT_A_NUMBER;
  if (Number(t) <= 0) return 'Amount must be greater than zero.';
  return null;
}

/** True when a required amount box holds a number the books can post. */
export function amountOk(text: string): boolean {
  return text.trim() !== '' && amountProblem(text) === null;
}

/**
 * The same rule for an OPTIONAL box — a network fee, the desk's own cost
 * inside a payment — where blank and zero both mean "none". Only the shape is
 * checked, so a typo can never quietly become nothing: a fee typed as "0,5"
 * used to fold to 0 and the coins the chain burned stayed in the books.
 */
export function optionalAmountProblem(text: string): string | null {
  const t = text.trim();
  if (t === '') return null;
  if (!PLAIN_DECIMAL.test(t)) return NOT_A_NUMBER;
  if (Number(t) < 0) return 'Amount cannot be negative.';
  return null;
}

// ── THE RATE BOX, THE SAME WAY ─────────────────────────────────────────────
// Three screens carried their own copy of this (Convert balance, Sub-broker,
// the cheque deal) and Book trade had none, so a rate pasted as "3,67" reached
// the books from one door and was refused at another (sweep, 13 Sept 2026).
// The calculator cleans grouping commas and currency tags on the way in
// (lib/trade-math.ts canonicalAmount); whatever is still not a plain decimal
// is named here, at the box, before anything is sent.
const PLAIN_RATE = /^(\d+(\.\d*)?|\.\d+)$/;
export function rateProblem(text: string): string | null {
  const t = text.trim();
  if (t === '') return null;
  if (!PLAIN_RATE.test(t)) return 'Rate must be a plain number — no commas, no spaces, no letters.';
  if (Number(t) <= 0) return 'Rate must be greater than zero.';
  return null;
}
/** True when the rate box holds a number the books can post. */
export function rateOk(text: string): boolean {
  return text.trim() !== '' && rateProblem(text) === null;
}
